import { useEffect, useMemo, useState } from "react";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import {
  Users as UsersIcon, Plus, Pencil, KeyRound, Trash2, RotateCcw, X, Save,
  Copy, ShieldCheck, Search, Star,
} from "lucide-react";
import { StatusBadge } from "@/components/Primitives";

// User Management. Before this screen existed the app could LIST its people and
// change nothing about them: no new technician, no role change on promotion, no
// password reset for someone locked out, no way to cut off a leaver. Every row
// here carries the four actions the fleet standard requires — edit, reset,
// deactivate, delete — and delete is soft, audited and restorable.

const inputCls =
  "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 focus:border-zinc-600 outline-none";

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className={`bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl w-full ${wide ? "max-w-lg" : "max-w-md"} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-900 sticky top-0 bg-zinc-950">
          <div className="text-sm font-medium text-zinc-100">{title}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

// A generated password is shown exactly once. If the operator cannot copy it out
// of this screen it may as well not have been generated.
function SecretOnce({ value, onClose }) {
  return (
    <Modal title="Password — shown once" onClose={onClose}>
      <p className="text-[13px] text-zinc-400 mb-3">
        Copy this now and hand it over. It is not stored in readable form and cannot be shown again.
      </p>
      <div className="flex items-stretch gap-2">
        <code className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 font-mono text-sm text-emerald-300 break-all">
          {value}
        </code>
        <button
          onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }}
          className="px-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200" title="Copy">
          <Copy size={15} />
        </button>
      </div>
      <button onClick={onClose}
        className="mt-4 w-full py-2 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm">
        I have copied it
      </button>
    </Modal>
  );
}

const EMPTY = { email: "", name: "", role: "l1_technician", title: "", client_id: "", password: "" };

export default function Users() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [secret, setSecret] = useState(null);
  // email (lowercased) -> vip_contacts row id, so the star toggle knows
  // whether to POST (tag) or DELETE (untag) and which row to delete.
  // VIP-ness lives on the email (backend/requesters.annotate_vip), not on
  // the user account, so this is a lookup against that separate list, not a
  // field on `u` itself.
  const [vipByEmail, setVipByEmail] = useState({});

  const clientRoles = useMemo(
    () => new Set(roles.filter((r) => r.group === "Client portal").map((r) => r.key)),
    [roles]);

  async function load() {
    setLoading(true);
    try {
      const [u, r, c, v] = await Promise.all([
        api.get(`/users?include_deleted=${showDeleted}`),
        api.get("/users/roles"),
        api.get("/clients"),
        api.get("/vip-contacts").catch(() => ({ data: [] })),
      ]);
      setRows(asArray(u.data));
      setRoles(asArray(r.data));
      setClients(asArray(c.data));
      setVipByEmail(Object.fromEntries(asArray(v.data).map((row) => [row.email, row.id])));
      setErr("");
    } catch (e) {
      // An unreachable list must not look like an empty one.
      setErr(e?.response?.data?.detail || e.message || "Could not load users");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showDeleted]);

  const shown = rows.filter((u) => {
    const s = `${u.name} ${u.email} ${u.role_label || u.role} ${u.title || ""}`.toLowerCase();
    return !q || s.includes(q.toLowerCase());
  });

  async function create() {
    try {
      const body = { ...form };
      if (!clientRoles.has(body.role)) delete body.client_id;
      if (!body.password) delete body.password;
      const r = await api.post("/users", body);
      toast.success(`Created ${r.data.email}`);
      if (r.data.initial_password) setSecret(r.data.initial_password);
      setCreating(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the account"); }
  }

  async function saveEdit() {
    try {
      const body = {
        name: editing.name, title: editing.title, role: editing.role,
        active: editing.active,
        client_id: clientRoles.has(editing.role) ? editing.client_id || null : null,
      };
      const r = await api.patch(`/users/${editing.id}`, body);
      const n = Object.keys(r.data?.changed || {}).length;
      toast.success(n ? `Updated ${n} field${n > 1 ? "s" : ""}` : "Nothing changed");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  }

  async function reset(u) {
    if (!window.confirm(`Reset the password for ${u.email}?\n\nThis also clears any lockout and signs their existing sessions out.`)) return;
    try {
      const r = await api.post(`/users/${u.id}/reset-password`, {});
      setSecret(r.data.password);
      toast.success("Password reset, lockout cleared, sessions revoked");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset"); }
  }

  async function remove(u) {
    if (!window.confirm(`Delete ${u.email}?\n\nThe account is kept for audit and refused at sign-in. You can restore it.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted — restorable from 'Show deleted'");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  }

  async function restore(u) {
    try { await api.post(`/users/${u.id}/restore`, {}); toast.success("Restored"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not restore"); }
  }

  // Tags/untags this account's OWN email in `vip_contacts` -- the single
  // list `ServiceDesk.jsx`'s VIP tab also manages. Every ticket that email
  // has ever raised (or ever will) picks the change up on its next read;
  // nothing on the ticket itself is touched here.
  async function toggleVip(u) {
    const email = (u.email || "").toLowerCase();
    const existingId = vipByEmail[email];
    try {
      if (existingId) {
        await api.delete(`/vip-contacts/${existingId}`);
        toast.success(`${u.email} is no longer VIP`);
      } else {
        await api.post("/vip-contacts", { email, name: u.name });
        toast.success(`${u.email} tagged VIP`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update VIP tag"); }
  }

  const assignable = roles.filter((r) => r.assignable);

  return (
    <div className="p-6" data-testid="users-page">
      <div className="flex items-center gap-3 mb-5">
        <UsersIcon size={18} className="text-zinc-400" />
        <div>
          <h1 className="text-lg font-display font-semibold text-zinc-50">User Management</h1>
          <div className="text-xs text-zinc-500">Staff and client-portal accounts for this tenant</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-2.5 text-zinc-600" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…"
              className="bg-zinc-900 border border-zinc-800 rounded pl-8 pr-3 py-1.5 text-sm text-zinc-200 outline-none focus:border-zinc-600 w-56" />
          </div>
          <label className="flex items-center gap-1.5 text-[12px] text-zinc-400 select-none">
            <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
            Show deleted
          </label>
          <button onClick={() => { setForm(EMPTY); setCreating(true); }} data-testid="user-new"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm">
            <Plus size={14} /> New user
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-4 border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-900">
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-left px-4 py-2.5">Role</th>
              <th className="text-left px-4 py-2.5">Client</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-600 text-sm">Loading…</td></tr>
            )}
            {!loading && !shown.length && !err && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-600 text-sm">No accounts match.</td></tr>
            )}
            {shown.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}
                className={`border-b border-zinc-900/70 hover:bg-zinc-900/40 ${u.deleted ? "opacity-50" : ""}`}>
                <td className="px-4 py-2.5 text-zinc-200">
                  <div className="flex items-center gap-1.5">
                    {vipByEmail[(u.email || "").toLowerCase()] && (
                      <Star size={12} className="text-amber-400 shrink-0" data-testid={`user-vip-badge-${u.id}`} />
                    )}
                    {u.name}
                  </div>
                  {u.title && <div className="text-[11px] text-zinc-600">{u.title}</div>}
                </td>
                <td className="px-4 py-2.5 font-mono text-[12.5px] text-zinc-400">{u.email}</td>
                <td className="px-4 py-2.5 text-zinc-300">{u.role_label || u.role}</td>
                <td className="px-4 py-2.5 text-zinc-500 text-[12.5px]">
                  {clients.find((c) => c.id === u.client_id)?.name || "—"}
                </td>
                <td className="px-4 py-2.5">
                  <StatusBadge status={u.deleted ? "retired" : u.active === false ? "disabled" : "active"} />
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    {u.deleted ? (
                      <button onClick={() => restore(u)} title="Restore"
                        className="p-1.5 rounded hover:bg-zinc-800 text-emerald-400"><RotateCcw size={14} /></button>
                    ) : (
                      <>
                        <button onClick={() => toggleVip(u)}
                          title={vipByEmail[(u.email || "").toLowerCase()] ? "Remove VIP tag" : "Tag as VIP"}
                          data-testid={`user-vip-toggle-${u.id}`}
                          className={`p-1.5 rounded hover:bg-zinc-800 ${vipByEmail[(u.email || "").toLowerCase()] ? "text-amber-400" : "text-zinc-600 hover:text-amber-400"}`}>
                          <Star size={14} fill={vipByEmail[(u.email || "").toLowerCase()] ? "currentColor" : "none"} />
                        </button>
                        <button onClick={() => setEditing({ ...u })} title="Edit"
                          data-testid={`user-edit-${u.id}`}
                          className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100"><Pencil size={14} /></button>
                        <button onClick={() => reset(u)} title="Reset password"
                          data-testid={`user-reset-${u.id}`}
                          className="p-1.5 rounded hover:bg-zinc-800 text-amber-400"><KeyRound size={14} /></button>
                        <button onClick={() => remove(u)} title="Delete"
                          data-testid={`user-delete-${u.id}`}
                          className="p-1.5 rounded hover:bg-zinc-800 text-red-400"><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <Modal title="New user" onClose={() => setCreating(false)} wide>
          <Field label="Full name">
            <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Email">
            <input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Job title">
            <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Role" hint={roles.find((r) => r.key === form.role)?.description}>
            <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {assignable.map((r) => <option key={r.key} value={r.key}>{r.group} · {r.label}</option>)}
            </select>
          </Field>
          {clientRoles.has(form.role) && (
            <Field label="Client" hint="A portal account sees only this client's data.">
              <select className={inputCls} value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                <option value="">Select a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Password" hint="Leave blank to generate one. 10+ characters with upper, lower and a digit.">
            <input className={inputCls} value={form.password} placeholder="(generate)"
              onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          <button onClick={create} data-testid="user-create-submit"
            className="mt-2 w-full py-2 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm flex items-center justify-center gap-2">
            <Save size={14} /> Create account
          </button>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit ${editing.email}`} onClose={() => setEditing(null)} wide>
          <Field label="Full name">
            <input className={inputCls} value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          </Field>
          <Field label="Job title">
            <input className={inputCls} value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
          </Field>
          <Field label="Role" hint={roles.find((r) => r.key === editing.role)?.description}>
            <select className={inputCls} value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
              {assignable.map((r) => <option key={r.key} value={r.key}>{r.group} · {r.label}</option>)}
              {!assignable.some((r) => r.key === editing.role) && (
                <option value={editing.role}>{editing.role} (cannot be reassigned by you)</option>
              )}
            </select>
          </Field>
          {clientRoles.has(editing.role) && (
            <Field label="Client">
              <select className={inputCls} value={editing.client_id || ""} onChange={(e) => setEditing({ ...editing, client_id: e.target.value })}>
                <option value="">Select a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          <div className="flex items-center justify-between py-2 border-t border-zinc-900 mt-2">
            <div>
              <div className="text-sm text-zinc-200">Active</div>
              <div className="text-[11px] text-zinc-600">Off = refused at sign-in and existing sessions revoked.</div>
            </div>
            <button onClick={() => setEditing({ ...editing, active: !editing.active })}
              className={`rounded-full transition-colors relative shrink-0 ${editing.active ? "bg-emerald-500/70" : "bg-zinc-700"}`}
              style={{ height: 22, width: 40 }}>
              <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white transition-all ${editing.active ? "left-[20px]" : "left-0.5"}`} />
            </button>
          </div>
          <button onClick={saveEdit} data-testid="user-edit-submit"
            className="mt-3 w-full py-2 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm flex items-center justify-center gap-2">
            <ShieldCheck size={14} /> Save changes
          </button>
        </Modal>
      )}

      {secret && <SecretOnce value={secret} onClose={() => setSecret(null)} />}
    </div>
  );
}
